import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Play, Pause, ArrowRight } from "lucide-react";
import "./About.css";
import { useNavigate } from "react-router-dom";


interface Service {
  name: string;
  fullName: string;
  description: string;
  route: string;
  gradient: string;
  icon: string;
  features: string[];
}

const services: Service[] = [
  {
    name: "FTO",
    fullName: "Freedom to Operate",
    description: "Navigate patent risks early and develop your innovation with confidence.",
    route: "/fto-report",
    gradient: "from-blue-600 via-blue-700 to-indigo-800",
    icon: "🔍",
    features: ["Patent Landscape Analysis", "Risk Assessment", "Strategic Recommendations"]
  },
  {
    name: "Prior Art",
    fullName: "Prior Art Search",
    description: "Understand the existing IP landscape and assess the patentability.",
    route: "/prior-art-report",
    gradient: "from-purple-600 via-purple-700 to-pink-800",
    icon: "🔬",
    features: ["Global Database Search", "Technical Literature Review", "Detailed Analysis Reports"]
  },
  {
    name: "Validity",
    fullName: "Patent Validity",
    description: "Evaluate the validity of competitor patents and identify vulnerabilities in their claims.",
    route: "/validity-search",
    gradient: "from-emerald-600 via-teal-700 to-cyan-800",
    icon: "⚖️",
    features: ["Patent Strength Analysis", "Invalidity Search", "Legal Risk Assessment"]
  },
  {
    name: "Compound Intelligence",
    fullName: "Compound Intelligence Services",
    description: "Analyze therapeutics to identify competing patents, therapies, and market opportunities.",
    route: "/compound-intelligence",
    gradient: "from-orange-600 via-red-700 to-pink-800",
    icon: "💊",
    features: ["Compound Analysis", "Target Identification", "Development Strategy"]
  },
];

const AllServices: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isPlaying && !isHovered) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % services.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, isHovered]);

  const goToSlide = (index: number): void => {
    setCurrentSlide(index);
  };

  const nextSlide = (): void => {
    setCurrentSlide((prev) => (prev + 1) % services.length);
  };

  // const prevSlide = (): void => {
  //   setCurrentSlide((prev) => (prev - 1 + services.length) % services.length);
  // };

  const toggleAutoplay = (): void => {
    setIsPlaying(!isPlaying);
  };

  const handleServiceClick = (route: string): void => {
    // You can implement navigation here
    console.log(`Navigating to ${route}`);
    navigate(route)
  };

  const getGradientStyle = (serviceName: string): string => {
    switch (serviceName) {
      case 'FTO':
        return 'linear-gradient(135deg, #1e40af 0%, #3730a3 100%)';
      case 'Prior Art':
        return 'linear-gradient(135deg, #7c3aed 0%, #be185d 100%)';
      case 'Validity':
        return 'linear-gradient(135deg, #059669 0%, #0891b2 100%)';
      case 'Compound Intelligence':
        return 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)';
      default:
        return 'linear-gradient(135deg, #1e40af 0%, #3730a3 100%)';
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>): void => {
    const target = e.currentTarget as HTMLElement;
    target.style.transform = 'scale(1.05)';
    target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>): void => {
    const target = e.currentTarget as HTMLElement;
    target.style.transform = 'scale(1)';
    target.style.boxShadow = '';
  };

  const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, isEnter: boolean): void => {
    const target = e.currentTarget as HTMLButtonElement;
    target.style.transform = isEnter ? 'scale(1.05)' : 'scale(1)';
  };

  return (
    <div className="py-2" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="container">
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="text-center mb-4">Our Services</h2>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            Comprehensive intellectual property and research solutions tailored to your needs
          </p>
        </div>

        {/* Main Carousel Container */}
        <div 
          className="position-relative rounded-3 overflow-hidden shadow-lg bg-white mb-5"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ maxWidth: '1200px', margin: '0 auto' }}
        >
          {/* Slides Container */}
          <div className="position-relative overflow-hidden" style={{ height: '450px' }}>
            {services.map((service: Service, index: number) => (
              <div
                key={service.name}
                className={`position-absolute top-0 start-0 w-100 h-100 transition-all ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  transform: index === currentSlide ? 'translateX(0)' : 
                            index < currentSlide ? 'translateX(-100%)' : 'translateX(100%)',
                  transition: 'all 0.7s ease-in-out'
                }}
              >
                <div 
                  className="w-100 h-100 position-relative overflow-hidden"
                  style={{
                    background: getGradientStyle(service.name)
                  }}
                >
                  {/* Background Pattern */}
                  <div className="position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.1 }}>
                    <div className="position-absolute rounded-circle border border-white border-3" 
                         style={{ top: '40px', left: '40px', width: '128px', height: '128px' }}></div>
                    <div className="position-absolute rounded-circle border border-white border-2" 
                         style={{ bottom: '80px', right: '80px', width: '96px', height: '96px' }}></div>
                    <div className="position-absolute rounded-circle border border-white border-2" 
                         style={{ top: '50%', right: '40px', width: '64px', height: '64px' }}></div>
                  </div>

                  {/* Content */}
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center" style={{ zIndex: 10, cursor: "pointer" }}
                    onClick={() => handleServiceClick(service.route)}
                  >
                    <div className="container">
                      <div className="row">
                        <div className="col-lg-8">
                          {/* Service Icon */}
                          <div className="mb-4" style={{ fontSize: '4rem' }}>{service.icon}</div>
                          
                          {/* Service Name */}
                          <h3 className="display-3 fw-bold text-white mb-2">
                            {service.name}
                          </h3>
                          <p className="h4 text-white mb-4" style={{ opacity: 0.9 }}>
                            {service.fullName}
                          </p>
                          
                          {/* Description */}
                          <p className="lead text-white mb-4" style={{ opacity: 0.8, maxWidth: '600px' }}>
                            {service.description}
                          </p>
                          
                          {/* Features */}
                          <div className="mb-4">
                            <div className="d-flex flex-wrap gap-2">
                              {service.features.map((feature: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="px-3 py-2 rounded-pill text-white border border-white"
                                  style={{ 
                                    backgroundColor: 'rgba(255,255,255,0.2)', 
                                    backdropFilter: 'blur(10px)',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          {/* CTA Button */}
                          {/* <button
                            onClick={() => handleServiceClick(service.route)}
                            className="btn btn-light btn-lg fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                            style={{ 
                              borderRadius: '50px',
                              transform: 'scale(1)',
                              transition: 'transform 0.3s ease'
                            }}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                          >
                            Get Started
                            <ArrowRight size={20} />
                          </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {/* <button
            onClick={prevSlide}
            className="position-absolute start-0 top-50 translate-middle-y ms-3 btn btn-light rounded-circle p-3 shadow-sm"
            style={{ zIndex: 20, opacity: 0.9 }}
            type="button"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button> */}
          
          <button
            onClick={nextSlide}
            className="hide-on-mobile position-absolute end-0 top-50 translate-middle-y me-3 rounded-circle p-3 shadow-sm"
            style={{ zIndex: 20, opacity: 0.9 }}
            type="button"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>

          {/* Autoplay Control */}
          <button
            onClick={toggleAutoplay}
            className="hide-on-mobile position-absolute top-0 end-0 mt-3 me-3 rounded-circle p-3 shadow-sm"
            style={{ zIndex: 20, opacity: 0.9 }}
            type="button"
            aria-label={isPlaying ? "Pause autoplay" : "Start autoplay"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>

          {/* Progress Bar */}
          <div className="position-absolute bottom-0 start-0 w-100 bg-white" style={{ height: '4px', opacity: 0.3 }}>
            <div
              className="h-100 bg-primary"
              style={{ 
                width: `${((currentSlide + 1) / services.length) * 100}%`,
                transition: 'width 0.3s ease'
              }}
            ></div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="d-flex justify-content-center gap-2 mb-4">
          {services.map((_: Service, index: number) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`border-0 rounded-pill ${
                index === currentSlide
                  ? 'bg-primary'
                  : 'bg-secondary'
              }`}
              style={{
                width: index === currentSlide ? '48px' : '12px',
                height: '12px',
                transition: 'all 0.3s ease',
                opacity: index === currentSlide ? 1 : 0.5
              }}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AllServices;